import { Alert, AlertDescription } from "design-system/atoms/Alert"
import { Button } from "design-system/atoms/Button"
import { AlertCircle, Loader2, Plug } from "lucide-react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { McpCapabilityConfigSchema, TestMcpResponseSchema } from "schema"
import { z } from "zod"
import { useTestMCPConnectionMutation } from "../../../hooks/useTestMCPConnectionMutation"

type MCPCapabilityFormType = z.infer<typeof McpCapabilityConfigSchema>
type DiscoveredTool = z.infer<typeof TestMcpResponseSchema>["tools"][number]

interface TestMCPConnectionProps {
  onToolsDiscovered?: (tools: DiscoveredTool[]) => void
  onTestStart?: () => void
}

export default function TestMCPConnection({
  onToolsDiscovered,
  onTestStart,
}: TestMCPConnectionProps) {
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext<MCPCapabilityFormType>()

  const { mutateAsync: testMCPConnection, isPending, data, error } = useTestMCPConnectionMutation()

  const transportType = watch("transportType")
  const serverUrl = watch("serverUrl")
  const authConfig = watch("authConfig")
  const connectionTimeoutMs = watch("connectionTimeoutMs")

  const handleTest = async () => {
    if (onTestStart) {
      onTestStart()
    }

    setValue("tools", [])

    const result = await testMCPConnection({
      transportType,
      serverUrl,
      authConfig,
      connectionTimeoutMs,
    })

    if (result.success && result.tools && result.tools.length > 0) {
      const toolsWithEnabled = result.tools.map((tool) => ({
        ...tool,
        enabled: true,
      }))
      setValue("tools", toolsWithEnabled)

      if (onToolsDiscovered) {
        onToolsDiscovered(result.tools)
      }
    }
  }

  const canTest = Boolean(serverUrl)

  return (
    <div className="space-y-4">
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={!canTest || isPending}
        onClick={handleTest}
      >
        {isPending ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {t("capabilities.mcpConfig.testConnection.testing")}
          </>
        ) : (
          <>
            <Plug className="size-4" />
            {t("capabilities.mcpConfig.testConnection.button")}
          </>
        )}
      </Button>
      {data && (
        <div className="space-y-4">
          {data.success ? (
            <Alert variant="default">
              <AlertDescription className="text-success">
                {t("capabilities.mcpConfig.testConnection.success")} {"- "}
                {t("capabilities.mcpConfig.testConnection.toolsDiscovered", {
                  count: data.toolsDiscovered,
                })}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Alert variant="destructive" className="flex items-center gap-2">
                <AlertDescription className="flex items-center gap-2">
                  <AlertCircle className="size-4" />
                  {t("capabilities.mcpConfig.testConnection.error")}
                </AlertDescription>
              </Alert>
              {data.errors && data.errors.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h4 className="text-sm font-medium">
                    {t("capabilities.mcpConfig.testConnection.errorsFound", {
                      count: data.errors.length,
                    })}
                  </h4>
                  <div className="flex flex-col gap-2">
                    {data.errors.map((error, index) => (
                      <div key={index} className="rounded-md border border-destructive p-3">
                        <div className="text-sm font-medium text-foreground">{error.field}</div>
                        <p className="text-sm text-muted-foreground">{error.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : t("capabilities.mcpConfig.testConnection.error")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
